/**
 * Zod schema for the YAML sidecar that carries an api_sequence's full DSL
 * (steps, exports, validations). Mirrors `ApiSequenceConfig` in
 * `packages/types/src/api-sequence.ts` 1:1 — we mirror rather than import to
 * keep `@chatsdet/canary-spec` dependency-free of the runtime types package.
 *
 * The sidecar is referenced from the spec's api_sequence node via:
 *
 *   **API sequence — Provision tenant**
 *   - apiSequence: ./sequences/provision-tenant.yaml
 *
 * Multiple api_sequence nodes in the same workflow point at the same sidecar
 * (DB schema enforces one api_sequence row per workflow). For now the file
 * carries ids verbatim (apiRequestId, credentialId) — name → id resolution
 * lands in a follow-up; the round-trip stays clean either way because the
 * ids come straight from the live workflow on pull.
 *
 * @module spec/api-sequence-file
 */

import { z } from 'zod';

const ApiSequenceValidationSchema = z
  .object({
    kind: z.string().min(1).optional(),
  })
  .passthrough();

const ApiSequenceStepSchema = z.object({
  stepId: z.string().min(1).optional(),
  apiRequestId: z.string().min(1),
  credentialId: z.string().nullable().optional(),
  // Auth by credential template/persona (resolved per-env); precedence over
  // credentialId. Preserved verbatim through the sidecar round-trip.
  credentialTemplateId: z.string().nullable().optional(),
  // Author-facing persona name; server resolves it to credentialTemplateId on save.
  credentialTemplateName: z.string().nullable().optional(),
  method: z.string().min(1).optional(),
  urlTemplate: z.string().optional(),
  queryParams: z
    .array(z.object({ key: z.string().min(1), value: z.string() }))
    .optional(),
  bodyTemplate: z.string().nullable().optional(),
  headersTemplate: z.record(z.string(), z.string()).nullable().optional(),
  responseVariableName: z.string().min(1).optional(),
  expectedStatusCodes: z.array(z.number().int()).optional(),
  description: z.string().optional(),
  validations: z.array(ApiSequenceValidationSchema).optional(),
});

const ApiSequenceExportSchema = z.object({
  variableName: z.string().min(1),
  stepId: z.string().min(1),
  responsePath: z.string().min(1),
  source: z.enum(['request', 'response']).optional(),
  description: z.string().optional(),
});

export const ApiSequenceFileSchema = z.object({
  description: z.string().optional(),
  stopOnFailure: z.boolean().optional(),
  steps: z.array(ApiSequenceStepSchema).default([]),
  exports: z.array(ApiSequenceExportSchema).default([]),
});

export type ApiSequenceFile = z.infer<typeof ApiSequenceFileSchema>;
